import { View } from '@play-co/timestep-core/ui';
import { getApplication, waitForItPromise } from 'src/lib/utils';
import gsap from 'gsap';
import { randomRange } from '../minigame/match2-timestep/odie';
import { ImageDescriptor } from '@play-co/timestep-core';
import { getBurstPool, getIconPool, animateScore } from './effects';
import sounds from 'src/lib/sounds';
import { throttle } from './animation.util';

const ZINDEX = 10001; // Over popups

const sfxProgress = throttle(
  () => sounds.playSound('progressCounted', 0.5),
  100,
);

const sfxGachaItemDrop = throttle(
  () => sounds.playSound('gachaItemDrop', 0.3),
  100,
);

export async function animateFlyingPoints(args: {
  count: number;
  target: { x: number; y: number };
  iconImage: string | ImageDescriptor;
  iconSize?: number;
  hideScore?: boolean;
  onLand?: (remaining: number) => void;
}) {
  const { count, target, iconImage, iconSize = 60, hideScore = false } = args;

  // Nothing to animate
  if (count <= 0) return;

  const root = getApplication().getRootView();
  const shortBurst = getBurstPool().obtainView();
  const bigBurst = getBurstPool().obtainView();

  sfxGachaItemDrop();

  // Limit the amount of points to 100, for animation purposes
  // Since this is a visual effect, we don't need to show all points
  const cappedCount = Math.min(count, 20);

  const origin = {
    x: root.style.width * 0.5,
    y: root.style.height * 0.5,
  };

  // Play a big burts on the center of the screen
  bigBurst.updateOpts({
    superview: root,
    x: origin.x,
    y: origin.y,
    zIndex: ZINDEX,
    scale: 2,
  });
  bigBurst.play('eventicon_burst', () => {
    bigBurst.removeFromSuperview();
    getBurstPool().releaseView(bigBurst);
  });

  // Here we should display the actual count
  if (!hideScore) {
    animateScore({
      superview: root,
      from: origin,
      count,
      zIndex: ZINDEX + 1,
    });
  }

  // Play all flying points
  const perIcon = count / cappedCount;
  for (let i = 0; i < cappedCount; i++) {
    const from = {
      x: origin.x + randomRange(-100, 100),
      y: origin.y + randomRange(-100, 100),
    };

    const to = {
      x: target.x,
      y: target.y,
    };

    playFlyingPoint({
      superview: root,
      from,
      to,
      iconImage,
      iconSize,
    }).then(() => {
      const remaining = Math.round(Math.max(0, count - perIcon * (i + 1)));
      args.onLand?.(remaining);
      shortBurst.updateOpts({
        superview: root,
        x: to.x,
        y: to.y,
        zIndex: ZINDEX,
        scale: 1,
      });
      shortBurst.play('currencycollect_impact_final', () => {
        shortBurst.removeFromSuperview();
        getBurstPool().releaseView(shortBurst);
      });
    });

    // Wait a bit before playing the next point
    await waitForItPromise(20);
  }

  // Complete after 1 second
  await waitForItPromise(1000);
}

async function playFlyingPoint(args: {
  superview: View;
  from: { x: number; y: number };
  to: { x: number; y: number };
  iconImage: string | ImageDescriptor;
  iconSize: number;
}) {
  const { from, to, iconImage, iconSize } = args;
  const icon = getIconPool().obtainView();
  icon.updateOpts({
    superview: args.superview,
    width: iconSize,
    height: iconSize,
    centerOnOrigin: true,
    scaleMethod: 'contain',
    image: iconImage,
    x: from.x,
    y: from.y,
    scale: 0,
    zIndex: ZINDEX,
  });

  const distanceX = to.x - from.x;
  const distanceY = to.y - from.y;

  // Spawn
  gsap.to(icon.style, {
    scale: 2,
    duration: 0.3,
    ease: 'back.out',
  });

  // Antecipate
  await gsap.to(icon.style, {
    x: from.x - distanceX * 0.2,
    y: from.y - distanceY * 0.2,
    duration: 0.5,
    ease: 'quad.out',
  });

  // Move to destination
  await gsap.to(icon.style, {
    x: to.x,
    y: to.y,
    scale: 1,
    duration: 0.5,
    ease: 'quad.in',
  });

  sfxProgress();
  icon.removeFromSuperview();
  getIconPool().releaseView(icon);
}
